import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BsFillCreditCardFill } from 'react-icons/bs';
import { toast } from 'react-toastify';
import styles from './style.module.scss';
import { useCart } from '../../contexts/CartProvider';
import { useSaldo } from '../../contexts/SaldoContext';
import { useGirosGanhos } from '../../contexts/GirosGanhosContext';
import { useCheckoutTotals, formatCurrency } from '../../hooks/useCheckoutTotals';
import { api } from '../../services/apiClient';
import CheckoutBase from '../CheckoutBase';
import CreditCard from '../CartaoGiratorio';

const MP_PUBLIC_KEY = process.env.REACT_APP_MP_TOKEN || 'APP_USR-c69facd8-08eb-4dfc-b299-f86744ee816a';

const digitos = (v) => (v || '').replace(/\D/g, '');

/** Luhn: pega número digitado errado antes de gastar uma ida ao Mercado Pago. */
const cartaoValido = (numero) => {
  const n = digitos(numero);
  if (n.length < 13 || n.length > 19) return false;

  let soma = 0;
  let dobra = false;
  for (let i = n.length - 1; i >= 0; i -= 1) {
    let d = Number(n[i]);
    if (dobra) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    soma += d;
    dobra = !dobra;
  }
  return soma % 10 === 0;
};

const validadeValida = (valor) => {
  const [mes, ano] = (valor || '').split('/');
  if (!mes || !ano || mes.length !== 2 || ano.length !== 2) return false;

  const mesNum = Number(mes);
  if (mesNum < 1 || mesNum > 12) return false;

  const agora = new Date();
  const limite = new Date(2000 + Number(ano), mesNum, 1);
  return limite > agora;
};

const cpfValido = (valor) => {
  const d = digitos(valor);
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;

  const dv = (tamanho) => {
    let soma = 0;
    for (let i = 0; i < tamanho; i += 1) soma += Number(d[i]) * (tamanho + 1 - i);
    return ((soma * 10) % 11) % 10;
  };

  return dv(9) === Number(d[9]) && dv(10) === Number(d[10]);
};

const CartaoCredito = ({ items, handleNextStep, couponCode, setCouponCode, validarCupon }) => {
  const [isSaldoRoute] = useState(() => window.location.href.includes('/saldo'));

  const { clearCart, coupon, setTotal } = useCart();
  const { fetchSaldo } = useSaldo();
  const { mostrarGirosGanhos } = useGirosGanhos();

  const [cardNumber, setCardNumber] = useState('');
  const [cardHolderName, setCardHolderName] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [cpf, setCpf] = useState('');
  const [parcelas, setParcelas] = useState('');
  const [loading, setLoading] = useState(false);
  const [erros, setErros] = useState({});

  const resumo = useCheckoutTotals(items, coupon);

  /**
   * O emissor do cartão depende só dos 6 primeiros dígitos (BIN). Antes essa busca
   * acontecia depois do clique em "Confirmar", somando um round-trip ao caminho crítico.
   * Agora ela dispara enquanto o cliente ainda digita e o resultado já está pronto na hora do envio.
   */
  const binRef = useRef({ bin: '', promise: null });

  const buscarEmissor = useCallback((bin) => {
    if (binRef.current.bin === bin && binRef.current.promise) return binRef.current.promise;

    const promise = fetch(
      `https://api.mercadopago.com/v1/payment_methods/search?public_key=${MP_PUBLIC_KEY}&locale=pt-BR&bins=${bin}`,
      { headers: { 'Content-Type': 'application/json' } }
    )
      .then((r) => r.json())
      .catch(() => null);

    binRef.current = { bin, promise };
    return promise;
  }, []);

  useEffect(() => {
    const bin = digitos(cardNumber).slice(0, 6);
    if (bin.length === 6) buscarEmissor(bin);
  }, [cardNumber, buscarEmissor]);

  const validarCampos = () => {
    const novos = {};
    if (!cartaoValido(cardNumber)) novos.cardNumber = 'Confira o número do cartão';
    if (cardHolderName.trim().length < 3) novos.cardHolderName = 'Digite o nome como está no cartão';
    if (!validadeValida(expiryDate)) novos.expiryDate = 'Validade inválida ou vencida';
    if (digitos(cvv).length < 3) novos.cvv = 'CVV incompleto';
    if (!cpfValido(cpf)) novos.cpf = 'CPF inválido';
    if (!parcelas) novos.parcelas = 'Escolha o parcelamento';

    setErros(novos);
    return Object.keys(novos).length === 0;
  };

  const pagar = async () => {
    if (!validarCampos()) {
      toast.warn('Confira os campos destacados do cartão.', { position: 'top-right', autoClose: 4000, theme: 'light' });
      return;
    }

    setLoading(true);

    try {
      const bin = digitos(cardNumber).slice(0, 6);
      // Emissor já foi buscado durante a digitação; aqui normalmente resolve na hora.
      const [resultToken, metodos] = await Promise.all([
        fetch(`https://api.mercadopago.com/v1/card_tokens?public_key=${MP_PUBLIC_KEY}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            card_number: digitos(cardNumber),
            cardholder: { name: cardHolderName, identification: { type: 'CPF', number: digitos(cpf) } },
            security_code: cvv,
            expiration_month: expiryDate.split('/')[0],
            expiration_year: `20${expiryDate.split('/')[1]}`,
          }),
        }).then((r) => r.json()),
        buscarEmissor(bin),
      ]);

      if (!resultToken?.id) {
        toast.error('Não foi possível validar o cartão. Confira os dados e tente de novo.', { position: 'top-right', autoClose: 5000, theme: 'light' });
        setLoading(false);
        return;
      }

      if (!metodos?.results?.length) {
        toast.error('Não reconhecemos a bandeira deste cartão.', { position: 'top-right', autoClose: 5000, theme: 'light' });
        setLoading(false);
        return;
      }

      const issuerId = metodos.results[0].issuer.id;
      const paymentMethodId = metodos.results[0].id;

      if (isSaldoRoute) {
        const responsePayment = await api.post('/carrega_saldo', {
          cpfCnpjCartaoCredito: digitos(cpf),
          nomeTitularCartao: cardHolderName,
          quantidadeParcelasPagamento: parcelas,
          tipoDocumento: 'CPF',
          tokenCard: resultToken.id,
          issuerId,
          paymentMethodId,
          total: resumo.total,
        });

        setTotal(resumo.total);

        if (responsePayment.status === 201) {
          toast.success('Compra feita com sucesso', { position: 'top-right', autoClose: 5000, theme: 'light' });
          fetchSaldo();
          handleNextStep(responsePayment.data.id);
          clearCart();
        }

        setLoading(false);
        return;
      }

      const responsePayment = await api.post('/comprar', {
        cupom_desconto_id: coupon?.id,
        boloes: items,
        quantity: resumo.quantidade,
        cpfCnpjCartaoCredito: digitos(cpf),
        nomeTitularCartao: cardHolderName,
        quantidadeParcelasPagamento: parcelas,
        total: resumo.subtotal,
        tokenCard: resultToken.id,
        issuerId,
        paymentMethodId,
      });

      if (responsePayment.status === 201) {
        if (responsePayment.data.status === 'rejected') {
          toast.error('Pagamento recusado pelo banco. Confira os dados ou use outro cartão.', { position: 'top-right', autoClose: 5000, theme: 'light' });
          setLoading(false);
          return;
        }

        if (responsePayment.data.status !== 'in_process') {
          toast.success('Compra feita com sucesso', { position: 'top-right', autoClose: 5000, theme: 'light' });
          clearCart();
          window.scrollTo(0, 0);
          fetchSaldo();
          handleNextStep(responsePayment.data.id);
          mostrarGirosGanhos(responsePayment.data.giros_creditados);
        }
      }
    } catch (error) {
      console.log('Erro ao processar pagamento', error);
      toast.error('Erro ao processar pagamento', { position: 'top-right', autoClose: 5000, theme: 'dark' });
    } finally {
      setLoading(false);
    }
  };

  const limparErro = (campo) => setErros((prev) => (prev[campo] ? { ...prev, [campo]: '' } : prev));

  return (
    <CheckoutBase
      resumo={resumo}
      couponCode={couponCode}
      setCouponCode={setCouponCode}
      validarCupon={validarCupon}
      mostrarCupom={!isSaldoRoute}
      loading={loading}
      onConfirm={pagar}
      confirmLabel={`Pagar ${formatCurrency(resumo.total)}`}
    >
      <div className={styles.cardBox}>
        <div className={styles.sectionTitle}>
          <BsFillCreditCardFill />
          <h2>Dados do cartão</h2>
        </div>
        <p className={styles.sectionDescription}>
          Preencha exatamente como está impresso no cartão para evitar recusas do banco.
        </p>

        <div className={styles.cardForm}>
          <CreditCard
            cardNumber={cardNumber}
            setCardNumber={(v) => { setCardNumber(v); limparErro('cardNumber'); }}
            cardHolderName={cardHolderName}
            setCardHolderName={(v) => { setCardHolderName(v); limparErro('cardHolderName'); }}
            expiryDate={expiryDate}
            setExpiryDate={(v) => { setExpiryDate(v); limparErro('expiryDate'); }}
            cvv={cvv}
            setCvv={(v) => { setCvv(v); limparErro('cvv'); }}
            cpf={cpf}
            setCpf={(v) => { setCpf(v); limparErro('cpf'); }}
            parcelas={parcelas}
            setParcelas={(v) => { setParcelas(v); limparErro('parcelas'); }}
          />
        </div>

        {Object.values(erros).some(Boolean) && (
          <ul className={styles.errorList} role='alert'>
            {Object.entries(erros)
              .filter(([, msg]) => msg)
              .map(([campo, msg]) => (
                <li key={campo}>{msg}</li>
              ))}
          </ul>
        )}
      </div>
    </CheckoutBase>
  );
};

export default CartaoCredito;
